(() => {
  if (window.__jmgFreeGiftLoaded) return;
  window.__jmgFreeGiftLoaded = true;

  const config = window.JmgFreeGiftConfig || {};
  const root = window.Shopify?.routes?.root || '/';
  const cartUrl = `${root}cart.js`;
  const addUrl = `${root}cart/add.js`;
  const changeUrl = `${root}cart/change.js`;
  const updateUrl = `${root}cart/update.js`;
  const nativeFetch = window.fetch.bind(window);
  const giftProperty = '_jmg_free_gift';
  const validVariantIds = new Set((config.variantIds || [config.variantId]).map(Number).filter((id) => id > 0));
  let preferredVariantId = Number(config.variantId);
  let syncTimer;
  let syncing = false;
  let rerunRequested = false;
  let internalRequest = false;

  const requestJson = async (url, options = {}) => {
    internalRequest = true;

    try {
      const response = await nativeFetch(url, {
        credentials: 'same-origin',
        ...options,
        headers: {
          Accept: 'application/json',
          ...(options.body ? { 'Content-Type': 'application/json' } : {}),
          ...(options.headers || {})
        }
      });

      if (!response.ok) throw new Error(`Cart request failed (${response.status})`);
      return response.json();
    } finally {
      internalRequest = false;
    }
  };

  const isGift = (item) => String(item.properties?.[giftProperty]) === 'true';
  const isConfiguredGift = (item) => isGift(item) && validVariantIds.has(Number(item.variant_id));

  const qualifyingSubtotal = (cart) => cart.items.reduce((total, item) => {
    return isGift(item) ? total : total + item.original_line_price;
  }, 0);

  const customerDiscountCodes = (cart) => (cart.discount_codes || [])
    .map((discount) => discount.code)
    .filter(Boolean)
    .filter((code) => code.toLowerCase() !== String(config.discountCode || '').toLowerCase());

  const setDiscountCodes = async (cart, includeGiftCode) => {
    const codes = customerDiscountCodes(cart);

    if (includeGiftCode && config.discountCode) codes.push(config.discountCode);
    if (!config.discountCode) return cart;

    return requestJson(updateUrl, {
      method: 'POST',
      body: JSON.stringify({ discount: codes.join(',') })
    });
  };

  const removeGiftLines = async (cart, giftLines) => {
    if (!giftLines.length) return cart;

    const updates = {};
    giftLines.forEach((item) => { updates[item.key] = 0; });

    return requestJson(updateUrl, {
      method: 'POST',
      body: JSON.stringify({ updates })
    });
  };

  const addGift = (variantId = preferredVariantId) => requestJson(addUrl, {
    method: 'POST',
    body: JSON.stringify({
      items: [{
        id: Number(variantId),
        quantity: 1,
        properties: {
          [giftProperty]: 'true',
          _jmg_free_gift_label: config.label || 'Regalo por tu compra',
          _jmg_free_gift_discount_code: config.discountCode || ''
        }
      }]
    })
  });

  const refreshCartPage = async () => {
    const currentSection = document.querySelector('.shopify-section--main-cart');
    if (!currentSection?.id) return;

    const sectionId = currentSection.id.replace('shopify-section-', '');
    const url = new URL(window.location.href);
    url.searchParams.set('section_id', sectionId);
    const html = await nativeFetch(url.toString(), { credentials: 'same-origin' }).then((response) => response.text());
    const replacement = new DOMParser().parseFromString(html, 'text/html').getElementById(currentSection.id);

    if (replacement) currentSection.innerHTML = replacement.innerHTML;
  };

  const refreshCartSurfaces = async () => {
    document.dispatchEvent(new CustomEvent('cart:refresh'));
    await refreshCartPage();
  };

  const synchronize = async () => {
    if (syncing) {
      rerunRequested = true;
      return;
    }

    syncing = true;
    let changed = false;

    try {
      let cart = await requestJson(cartUrl);
      const giftLines = cart.items.filter(isGift);
      const configuredGiftLines = giftLines.filter(isConfiguredGift);
      if (configuredGiftLines[0]) preferredVariantId = Number(configuredGiftLines[0].variant_id);
      const shouldHaveGift = Boolean(config.enabled)
        && validVariantIds.size > 0
        && Number(config.threshold) > 0
        && qualifyingSubtotal(cart) >= Number(config.threshold);

      if (!shouldHaveGift) {
        if (giftLines.length) {
          cart = await removeGiftLines(cart, giftLines);
          changed = true;
        }

        if (config.discountCode) {
          await setDiscountCodes(cart, false);
          changed = true;
        }
      } else {
        const obsoleteGiftLines = giftLines.filter((item) => !isConfiguredGift(item));
        const duplicateGiftLines = configuredGiftLines.slice(1);

        if (obsoleteGiftLines.length || duplicateGiftLines.length) {
          cart = await removeGiftLines(cart, [...obsoleteGiftLines, ...duplicateGiftLines]);
          changed = true;
        }

        const currentGift = cart.items.find(isConfiguredGift);

        if (currentGift && currentGift.quantity !== 1) {
          cart = await requestJson(changeUrl, {
            method: 'POST',
            body: JSON.stringify({ id: currentGift.key, quantity: 1 })
          });
          changed = true;
        }

        cart = await setDiscountCodes(cart, true);

        if (!cart.items.some(isConfiguredGift)) {
          await addGift(preferredVariantId);
          cart = await requestJson(cartUrl);
          changed = true;
        }

        const chargedGift = cart.items.find((item) => isGift(item) && item.final_line_price > 0);
        if (chargedGift) {
          await removeGiftLines(cart, [chargedGift]);
          changed = true;
          console.warn('[JMG regalo] El código configurado no ha dejado el regalo a 0 €. Se ha retirado para evitar cobrarlo.');
        }
      }

      if (changed) await refreshCartSurfaces();
    } catch (error) {
      console.warn('[JMG regalo] No se ha podido sincronizar el regalo automático.', error);
    } finally {
      syncing = false;

      if (rerunRequested) {
        rerunRequested = false;
        scheduleSync();
      }
    }
  };

  const scheduleSync = () => {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(synchronize, 150);
  };

  const changeGiftVariant = async (variantId, select) => {
    if (!validVariantIds.has(variantId) || variantId === preferredVariantId || syncing) return;

    syncing = true;
    select.disabled = true;

    try {
      preferredVariantId = variantId;
      let cart = await requestJson(cartUrl);
      cart = await removeGiftLines(cart, cart.items.filter(isGift));
      cart = await setDiscountCodes(cart, true);
      await addGift(variantId);
      cart = await requestJson(cartUrl);

      const chargedGift = cart.items.find((item) => isConfiguredGift(item) && item.final_line_price > 0);
      if (chargedGift) {
        await removeGiftLines(cart, [chargedGift]);
        throw new Error('The selected gift variant was not discounted');
      }

      await refreshCartSurfaces();
    } catch (error) {
      console.warn('[JMG regalo] No se ha podido cambiar la talla del regalo.', error);
    } finally {
      syncing = false;
      if (select.isConnected) select.disabled = false;
    }
  };

  const cartMutationPattern = /\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/;
  window.fetch = async (...args) => {
    const response = await nativeFetch(...args);
    const requestUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';

    if (!internalRequest && cartMutationPattern.test(requestUrl)) scheduleSync();
    return response;
  };

  document.addEventListener('cart:change', scheduleSync);
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-jmg-free-gift-variant-selector]');
    if (select) changeGiftVariant(Number(select.value), select);
  });
  window.addEventListener('pageshow', scheduleSync);
  scheduleSync();
})();
